export type EcommerceItem = Record<string, any>;

export type MappedItem = {
  product_id: string;
  sku: string;
  name: string;
  qty: number;
  price: number;
  brand: string;
  category: string;
  categories: string[];
  variant: string;
};

function asObject(value: unknown): Record<string, any> {
  return value && typeof value === 'object' ? value as Record<string, any> : {};
}

function firstCatalogId(item: EcommerceItem, ext: Record<string, any>): string {
  const candidates = [
    item.catalog_product_id,
    ext.catalog_product_id,
    item.product_id,
    ext.product_id,
    item.sku,
    ext.sku,
  ];

  for (const value of candidates) {
    if (value === null || value === undefined || value === '' || value === 0 || value === '0') {
      continue;
    }

    return String(value);
  }

  return '';
}

function categories(item: EcommerceItem, ext: Record<string, any>): string[] {
  let raw = item.category_names ?? item.category ?? ext.category_names ?? ext.categories ?? [];
  if (typeof raw === 'string') {
    if (raw === '') {
      return [];
    }
    try {
      const decoded = JSON.parse(raw);
      raw = Array.isArray(decoded) ? decoded : raw.split(/\s*\/\s*/);
    } catch (e) {
      raw = raw.split(/\s*\/\s*/);
    }
  }

  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.map((value) => String(value)).filter(Boolean);
}

export function mapItem(item: EcommerceItem): MappedItem {
  const ext = asObject(item.extension_attributes);
  const qty = Number(item.qty ?? item.qty_ordered ?? 1) || 1;
  const unit = Number(item.price_incl_tax ?? item.base_price_incl_tax ?? item.row_price_incl_tax ?? 0) || 0;
  const productId = firstCatalogId(item, ext);
  const itemCategories = categories(item, ext);

  return {
    product_id: String(productId),
    sku: String(item.sku ?? ext.sku ?? ''),
    name: String(item.name ?? ''),
    qty,
    price: unit,
    brand: String(item.brand ?? item.manufacturer ?? ext.manufacturer ?? ext.brand ?? ''),
    category: itemCategories.join(' / '),
    categories: itemCategories,
    variant: String(item.variant ?? ext.color ?? ''),
  };
}

export function toQuoteItems(items: Array<EcommerceItem> | undefined | null): MappedItem[] {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.filter((item) => item && typeof item === 'object').map(mapItem);
}

export function toGa4Items(items: Array<EcommerceItem> | undefined | null): Array<Record<string, any>> {
  return toQuoteItems(items).map((row, index) => {
    const ga4: Record<string, any> = {
      item_name: row.name,
      item_id: row.product_id,
      item_brand: row.brand,
      quantity: row.qty,
      index: index + 1,
      price: Math.round(row.price * 100) / 100,
    };

    row.categories.forEach((category, i) => {
      ga4[i === 0 ? 'item_category' : `item_category${i + 1}`] = category;
    });

    return ga4;
  });
}

export function toUaProducts(items: Array<EcommerceItem> | undefined | null): Array<Record<string, any>> {
  return toQuoteItems(items).map((row) => ({
    name: row.name,
    id: row.product_id,
    price: String(row.price),
    sku: row.sku,
    brand: row.brand,
    category: row.category,
    variant: row.variant,
    quantity: String(row.qty),
  }));
}
